import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { Skeleton } from '@/components/feedback/skeleton'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { listAdminApps, type AdminApp } from '@/features/admin/apps/api'
import { BusinessWorkspaceIcon } from '@/features/user/shell/navigation-icons'

import {
  getBrandingSettings,
  updateBusinessNavigationSettings,
  type AdminSystemSettings,
} from './api'

interface BusinessNavigationFormState {
  applicationId: string
  enabled: boolean
  label: string
  url: string
}

type BusinessNavigationErrors = Partial<Record<'label' | 'url', string>>

const queryKey = ['admin-business-navigation-settings']

function readFormState(settings: AdminSystemSettings): BusinessNavigationFormState {
  const businessNavigation = settings.config.business_navigation

  return {
    applicationId: businessNavigation?.application_id ?? '',
    enabled: businessNavigation?.enabled === true,
    label: businessNavigation?.label ?? '',
    url: businessNavigation?.url ?? '',
  }
}

function formKey(settings: AdminSystemSettings) {
  const state = readFormState(settings)
  return `${String(state.enabled)}:${state.label}:${state.url}:${state.applicationId}`
}

function isValidBusinessNavigationUrl(value: string) {
  if (value.length > 2048) {
    return false
  }
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'https:' && !parsed.username && !parsed.password
  } catch {
    return false
  }
}

function ExternalLinkIcon() {
  return (
    <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 14 14">
      <path
        d="M5 3h6v6M11 3 4 10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.25"
      />
    </svg>
  )
}

export function AdminBusinessNavigationPage() {
  const { t } = useTranslation('adminSettings')
  const settingsQuery = useQuery({ queryFn: getBrandingSettings, queryKey })

  return (
    <AdminShellPage activePath="/admin/settings/business-navigation">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        {settingsQuery.isError ? (
          <>
            <PageHeader
              contentClassName="max-w-[820px]"
              description={t('businessNavigation.description')}
              title={t('businessNavigation.title')}
            />
            <div className="flex flex-1 items-center justify-center px-6">
              <ErrorState
                description={t('businessNavigation.loadDescription')}
                onRetry={() => void settingsQuery.refetch()}
                title={t('businessNavigation.loadTitle')}
              />
            </div>
          </>
        ) : settingsQuery.isLoading || !settingsQuery.data ? (
          <>
            <PageHeader
              contentClassName="max-w-[820px]"
              description={t('businessNavigation.description')}
              title={t('businessNavigation.title')}
            />
            <div className="px-5 py-5 lg:px-[30px]">
              <PageContentBoundary className="max-w-[820px]">
                <Skeleton className="h-[360px] rounded-lg" />
              </PageContentBoundary>
            </div>
          </>
        ) : (
          <BusinessNavigationForm key={formKey(settingsQuery.data)} settings={settingsQuery.data} />
        )}
      </div>
    </AdminShellPage>
  )
}

function BusinessNavigationForm({ settings }: { settings: AdminSystemSettings }) {
  const { t } = useTranslation('adminSettings')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const initialState = useMemo(() => readFormState(settings), [settings])
  const [formState, setFormState] = useState(initialState)
  const [savedFormState, setSavedFormState] = useState(initialState)
  const [errors, setErrors] = useState<BusinessNavigationErrors>({})
  const appsQuery = useQuery({
    queryKey: ['admin-apps', 'navigation-options'],
    queryFn: async () => {
      const items: AdminApp[] = []
      for (let page = 1; ; page += 1) {
        const result = await listAdminApps({ page, pageSize: 100 })
        items.push(...result.items)
        if (!result.items.length || items.length >= result.total) return items
      }
    },
  })
  const isDirty =
    formState.applicationId !== savedFormState.applicationId ||
    formState.enabled !== savedFormState.enabled ||
    formState.label !== savedFormState.label ||
    formState.url !== savedFormState.url

  const saveMutation = useMutation({
    mutationFn: async () => {
      const label = formState.label.trim()
      const url = formState.url.trim()
      const nextErrors: BusinessNavigationErrors = {}

      if (formState.enabled && !label) {
        nextErrors.label = t('businessNavigation.validation.labelRequired')
      } else if (Array.from(label).length > 20) {
        nextErrors.label = t('businessNavigation.validation.labelTooLong')
      }
      if (formState.enabled && !url) {
        nextErrors.url = t('businessNavigation.validation.urlRequired')
      } else if (url && !isValidBusinessNavigationUrl(url)) {
        nextErrors.url = t('businessNavigation.validation.urlInvalid')
      }

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        window.requestAnimationFrame(() => {
          document
            .getElementById(
              nextErrors.label ? 'business-navigation-label' : 'business-navigation-url',
            )
            ?.focus()
        })
        return undefined
      }

      setErrors({})
      return updateBusinessNavigationSettings({
        enabled: formState.enabled,
        label,
        url,
        application_id: formState.applicationId || null,
      })
    },
    onError: () => {
      showFeedback({ message: t('businessNavigation.saveFailed'), tone: 'error' })
    },
    onSuccess: async (result) => {
      if (!result) return

      const nextState = readFormState(result)
      setFormState(nextState)
      setSavedFormState(nextState)
      await queryClient.invalidateQueries({ queryKey })
      await queryClient.invalidateQueries({ queryKey: ['user-business-navigation'] })
      showFeedback({ message: t('businessNavigation.saved'), tone: 'success' })
    },
  })

  const previewLabel = formState.label.trim() || t('businessNavigation.previewLabel')

  return (
    <>
      <PageHeader
        contentClassName="max-w-[820px]"
        description={t('businessNavigation.description')}
        title={t('businessNavigation.title')}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 lg:px-[30px]">
        <PageContentBoundary className="max-w-[820px]">
          <section className="grid gap-5 rounded-lg border border-[var(--color-border)] bg-white p-5 shadow-[0_1px_2px_rgba(26,28,25,0.04)]">
            <div className="flex items-start justify-between gap-5">
              <div className="grid gap-1">
                <h2 className="text-base font-bold text-[var(--color-text)]">
                  {t('businessNavigation.cardTitle')}
                </h2>
                <p className="text-sm leading-6 text-[var(--color-text-muted)]">
                  {t('businessNavigation.cardDescription')}
                </p>
              </div>
              <Switch
                aria-label={t('businessNavigation.enabled')}
                checked={formState.enabled}
                disabled={saveMutation.isPending}
                onCheckedChange={(enabled) => setFormState((current) => ({ ...current, enabled }))}
              />
            </div>

            <div className="grid gap-4 border-t border-[var(--color-border)] pt-5">
              <Field
                hint={t('businessNavigation.applicationHint')}
                htmlFor="business-navigation-application"
                label={t('businessNavigation.application')}
                required={false}
              >
                <Select
                  disabled={appsQuery.isPending || appsQuery.isError || saveMutation.isPending}
                  id="business-navigation-application"
                  onValueChange={(applicationId) =>
                    setFormState((current) => ({ ...current, applicationId }))
                  }
                  options={[
                    { label: t('businessNavigation.noApplication'), value: '' },
                    ...(formState.applicationId &&
                    !appsQuery.data?.some((app) => app.id === formState.applicationId)
                      ? [
                          {
                            label: t('businessNavigation.unavailableApplication'),
                            value: formState.applicationId,
                          },
                        ]
                      : []),
                    ...(appsQuery.data ?? []).map((app) => ({ label: app.name, value: app.id })),
                  ]}
                  value={formState.applicationId}
                />
                {appsQuery.isError ? (
                  <Button
                    onClick={() => void appsQuery.refetch()}
                    type="button"
                    variant="secondary"
                  >
                    {t('businessNavigation.retryApplications')}
                  </Button>
                ) : null}
              </Field>
              <Field
                error={errors.label}
                htmlFor="business-navigation-label"
                label={t('businessNavigation.label')}
                required={formState.enabled}
              >
                <Input
                  aria-describedby={errors.label ? 'business-navigation-label-error' : undefined}
                  id="business-navigation-label"
                  invalid={Boolean(errors.label)}
                  maxLength={20}
                  onChange={(event) => {
                    setFormState((current) => ({ ...current, label: event.target.value }))
                    setErrors((current) => ({ ...current, label: undefined }))
                  }}
                  placeholder={t('businessNavigation.labelPlaceholder')}
                  value={formState.label}
                />
              </Field>
              <Field
                error={errors.url}
                hint={t('businessNavigation.urlHint')}
                htmlFor="business-navigation-url"
                label={t('businessNavigation.url')}
                required={formState.enabled}
              >
                <Input
                  aria-describedby={
                    errors.url ? 'business-navigation-url-error' : 'business-navigation-url-hint'
                  }
                  id="business-navigation-url"
                  invalid={Boolean(errors.url)}
                  maxLength={2048}
                  onChange={(event) => {
                    setFormState((current) => ({ ...current, url: event.target.value }))
                    setErrors((current) => ({ ...current, url: undefined }))
                  }}
                  placeholder="https://adp.example.com/"
                  type="url"
                  value={formState.url}
                />
              </Field>
            </div>

            <div className="grid gap-2">
              <span className="text-sm font-medium text-[var(--color-text)]">
                {t('businessNavigation.preview')}
              </span>
              <div className="w-full max-w-[248px] rounded-xl bg-[var(--color-sidebar-bg)] p-2">
                <div className="grid h-10 grid-cols-[40px_minmax(0,1fr)_18px] items-center gap-2 rounded-lg pr-3 text-[15px] font-semibold text-[var(--color-sidebar-text)]">
                  <span className="flex size-[18px] items-center justify-center justify-self-center">
                    <BusinessWorkspaceIcon />
                  </span>
                  <span className="truncate">{previewLabel}</span>
                  <span className="flex size-[18px] items-center justify-center text-[var(--color-sidebar-text-muted)]">
                    <ExternalLinkIcon />
                  </span>
                </div>
              </div>
              <p className="text-xs leading-5 text-[var(--color-text-muted)]">
                {t('businessNavigation.previewHint')}
              </p>
            </div>

            <div className="flex justify-end border-t border-[var(--color-border)] pt-5">
              <Button
                disabled={!isDirty}
                isLoading={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {t('businessNavigation.save')}
              </Button>
            </div>
          </section>
        </PageContentBoundary>
      </div>
    </>
  )
}

function Field({
  children,
  error,
  hint,
  htmlFor,
  label,
  required,
}: {
  children: ReactNode
  error?: string
  hint?: string
  htmlFor: string
  label: string
  required: boolean
}) {
  return (
    <div className="grid gap-2">
      <label className="text-sm font-medium leading-5 text-[var(--color-text)]" htmlFor={htmlFor}>
        {label}
        {required ? <span className="ml-1 text-[var(--color-danger)]">*</span> : null}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-[var(--color-danger)]" id={`${htmlFor}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-[var(--color-text-muted)]" id={`${htmlFor}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}
